import type { AmlogicDeviceSession } from "../../core/amlogic/multi/AmlogicDeviceSession";
import type { AmlogicBatchRunSnapshot } from "../../core/amlogic/multi/AmlogicBatchRunStore";
import type { OperationProgress } from "../../core/types";
import { formatBytes } from "../../components/formatBytes";

interface Props {
  sessions: readonly AmlogicDeviceSession[];
  selectedIds: ReadonlySet<string>;
  eligibility: ReadonlyMap<string, string | undefined>;
  batch?: AmlogicBatchRunSnapshot;
  now: number;
  onSelect: (id: string, selected: boolean) => void;
  onConnect: (id: string) => void;
  onDisconnect: (id: string) => void;
  onRemove: (id: string) => void;
  onCancelJob: (id: string) => void;
  onSelectLog: (id: string) => void;
}

const STATUS: Record<string, string> = {
  discovered: "Đã phát hiện", connecting: "Đang kết nối", ready: "Sẵn sàng", queued: "Trong hàng đợi",
  running: "Đang flash", "waiting-reenumeration": "Chờ USB", reacquiring: "Kết nối lại",
  completed: "Hoàn tất", failed: "Lỗi", cancelled: "Đã hủy", disconnected: "Mất kết nối",
  skipped: "Bỏ qua",
};

function formatElapsed(start: number | undefined, end: number): string {
  if (!start) return "—";
  const seconds = Math.max(0, Math.floor((end - start) / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

function progressPercent(session: AmlogicDeviceSession): number | undefined {
  const progress = session.progress;
  if (!progress?.totalBytes) return undefined;
  return Math.min(100, Math.max(0, progress.completedBytes / progress.totalBytes * 100));
}

function formatSpeed(progress: OperationProgress | undefined, start: number | undefined, end: number): string {
  const measured = progress?.speedBytesPerSecond;
  const elapsedMs = start ? Math.max(0, end - start) : 0;
  const average = progress && progress.completedBytes > 0 && elapsedMs > 0
    ? progress.completedBytes / (elapsedMs / 1000)
    : undefined;
  const speed = measured && measured > 0 ? measured : average;
  return speed && Number.isFinite(speed) ? `${formatBytes(speed)}/s` : "—";
}

export function AmlogicDeviceTable(props: Props) {
  const selectable = props.sessions.filter((session) => props.eligibility.get(session.id) === undefined);
  const allSelected = selectable.length > 0 && selectable.every((session) => props.selectedIds.has(session.id));

  return (
    <div className="amlogic-table-scroll">
      <table className="amlogic-device-table">
        <thead><tr>
          <th className="amlogic-check-cell"><input type="checkbox" aria-label="Chọn tất cả thiết bị sẵn sàng" checked={allSelected} onChange={(event) => selectable.forEach((session) => props.onSelect(session.id, event.target.checked))} /></th>
          <th>Thiết bị</th><th>Trạng thái / tiến độ</th><th className="amlogic-time-cell">Thời gian / tốc độ</th><th>Kết quả</th><th>Thao tác</th>
        </tr></thead>
        <tbody>
          {props.sessions.length === 0 && <tr><td colSpan={6} className="amlogic-empty-row">Chưa có thiết bị thuộc thế hệ này.</td></tr>}
          {props.sessions.map((session) => {
            const target = props.batch?.targets.find((item) => item.sessionId === session.id);
            const reason = props.eligibility.get(session.id);
            const percent = progressPercent(session);
            const state = target?.state ?? session.status;
            const busy = ["queued", "running", "waiting-reenumeration", "reacquiring", "connecting"].includes(session.status);
            const elapsedEnd = target?.finishedAt ?? props.batch?.finishedAt ?? props.now;
            const elapsed = formatElapsed(target?.startedAt, elapsedEnd);
            const progress = session.progress ?? target?.progress;
            const speed = formatSpeed(progress, target?.startedAt ?? progress?.startedAt, elapsedEnd);
            const result = target?.error || session.error || (target ? STATUS[target.state] : "—");
            const resultIsError = Boolean(target?.error || session.error);
            return <tr key={session.id} className={props.selectedIds.has(session.id) ? "is-selected" : undefined} tabIndex={0} aria-label={`Lọc log ${session.label}`} onClick={() => props.onSelectLog(session.id)} onKeyDown={(event) => {
              if (event.target !== event.currentTarget || (event.key !== "Enter" && event.key !== " ")) return;
              event.preventDefault();
              props.onSelectLog(session.id);
            }}>
              <td className="amlogic-check-cell"><input type="checkbox" aria-label={`Chọn ${session.label}`} checked={props.selectedIds.has(session.id)} onClick={(event) => event.stopPropagation()} onChange={(event) => props.onSelect(session.id, event.target.checked)} /></td>
              <td><strong>{session.label}</strong><span className="amlogic-device-subline">{session.usbDevice?.serialNumber || session.info?.chipFamily || session.service.handshakeInfo?.stage || "USB Amlogic"}</span><span className="amlogic-mobile-time">{elapsed} · {speed}</span></td>
              <td>
                <span className={`amlogic-row-state amlogic-row-state--${state}`}>{STATUS[state] ?? state}</span>
                {state === "running" && <div className="amlogic-inline-progress" role="progressbar" aria-label={`Tiến độ ${session.label}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent === undefined ? undefined : Math.round(percent)}><span style={{ width: `${percent ?? 15}%` }} /></div>}
                <span className="amlogic-device-subline">{state === "running" ? `${session.progress?.phase ?? "Đang xử lý"}${percent === undefined ? "" : ` · ${percent.toFixed(1)}%`}` : reason}</span>
              </td>
              <td className="amlogic-time-cell"><span className="amlogic-time-stack"><strong>{elapsed}</strong><span className="amlogic-speed">{speed}</span></span></td>
              <td className={`amlogic-result-cell${resultIsError ? " amlogic-result-error" : ""}`}><span className="amlogic-result-message" title={resultIsError ? result : undefined} aria-label={resultIsError ? result : undefined}>{result}</span></td>
              <td className="amlogic-actions-cell" onClick={(event) => event.stopPropagation()}>
                <div className="amlogic-row-actions" role="group" aria-label={`Thao tác ${session.label}`}>
                  <button type="button" onClick={() => props.onConnect(session.id)} disabled={busy || session.status === "ready"}>Kết nối</button>
                  <button type="button" onClick={() => props.onDisconnect(session.id)} disabled={busy || session.status === "disconnected"}>Ngắt</button>
                  {busy ? <button type="button" onClick={() => props.onCancelJob(session.id)}>Hủy job</button> : <button type="button" onClick={() => props.onRemove(session.id)}>Xóa</button>}
                </div>
              </td>
            </tr>;
          })}
        </tbody>
      </table>
    </div>
  );
}
